import { type RefObject, useEffect } from 'react';

/**
 * Send a scan to the search field whatever was tapped last (#319).
 *
 * A scanner is a keyboard: it types the digits into whatever has focus, then
 * presses Enter. After a tap on a plate or a line's control, that is a button
 * — the digits go nowhere and the Enter **presses it**, which might be a line's
 * remove. So a printable key that lands on something that does not take text
 * is moved to the search field, and with it everything after: the Enter then
 * lands in the field, where it resolves the scan.
 *
 * **Never from a field somebody is typing in** — the cash given, the payment
 * reference, a line's quantity — and never from inside a dialog, which is
 * somebody else's question. Space is left alone because it presses a focused
 * button on purpose, and a barcode has none.
 *
 * `begin` receives the first key; the field's own value is replaced by it,
 * because whatever was left there belongs to an earlier, finished search.
 */
export function useScanCapture(
  field: RefObject<HTMLInputElement | null>,
  begin: (key: string) => void,
): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const input = field.current;

      if (input === null || event.defaultPrevented) return;
      if (event.key.length !== 1 || event.key === ' ') return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.target instanceof Element && !willRedirect(event.target)) return;

      event.preventDefault();
      input.focus();
      begin(event.key);
    };

    // Capture, so this sees the key before the focused control does.
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [field, begin]);
}

/** Input types that hold no text, so a key typed at one would be lost. */
const NOT_TEXT = new Set(['button', 'checkbox', 'radio', 'submit', 'reset', 'image', 'range']);

function willRedirect(target: Element): boolean {
  if (target.closest('[role="dialog"], [role="alertdialog"]') !== null) return false;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return false;
  if (target instanceof HTMLInputElement) return NOT_TEXT.has(target.type);
  if (target instanceof HTMLElement && target.isContentEditable) return false;

  return true;
}
